/**
 * Loads the backend's /api/v1/auth/config at boot and provides it to descendants.
 *
 * Until the call resolves we render a minimal "Loading…" state — MSAL can't
 * initialise without these values, so this is the single bootstrap gate.
 *
 * The context object and the `useAuthConfig` hook live in sibling files so
 * this module only exports a component (Vite fast-refresh rule).
 */

import { useEffect, useState } from "react";
import type { PropsWithChildren, ReactNode } from "react";
import { AuthConfigContext } from "./AuthConfigContext";
import { type AuthConfig, getAuthConfig } from "@/shared/api/auth-config";

interface Props {
  /** Override the loader for tests / Storybook. */
  load?: () => Promise<AuthConfig>;
  /** Optional fallback while loading. */
  fallback?: ReactNode;
}

export function AuthConfigProvider({
  children,
  load = getAuthConfig,
  fallback = <p>Loading configuration…</p>,
}: PropsWithChildren<Props>) {
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    load()
      .then((c) => {
        if (!cancelled) setConfig(c);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [load]);

  if (error) {
    return (
      <main style={{ padding: "2rem", fontFamily: "system-ui, sans-serif" }}>
        <h1>Configuration error</h1>
        <p>Could not load auth configuration: {error}</p>
      </main>
    );
  }
  if (!config) {
    return <>{fallback}</>;
  }
  return <AuthConfigContext.Provider value={config}>{children}</AuthConfigContext.Provider>;
}
